import { Pencil, UserX } from 'lucide-react';
import { type User, isAdmin } from '@peles/shared';
import { UserAvatar } from '../common/UserAvatar';
import { AuditInfoTooltip } from '../common/AuditInfoTooltip';
import { useViewMode } from '../../context/ViewModeContext';
import { useDialog } from '../../context/DialogContext';
import { RecordConversations } from '../messaging/RecordConversations';
import type { ThreadCount } from '../../hooks/useThreadCounts';

interface UserTableProps {
  users: User[];
  selectedUserId: string | null;
  onSelectUser: (id: string) => void;
  isLoading: boolean;
  currentUser?: User;
  onDeactivateUser?: (id: string) => void;
  /**
   * Each person's conversation counts, fetched once for the list by the page
   * (COMMUNICATIONS-DESIGN H3). Without it a row shows no conversation icon.
   */
  threadCountFor?: (userId: string) => ThreadCount | null;
  /** The counts' refetch, so a conversation started or read updates its badge. */
  onThreadActivity?: () => void;
}

export function UserTable({
  users,
  selectedUserId,
  onSelectUser,
  isLoading,
  currentUser,
  onDeactivateUser,
  threadCountFor,
  onThreadActivity,
}: UserTableProps) {
  const { confirm } = useDialog();

  if (isLoading) {
    return <div className="text-gray-500 text-sm py-8 text-center">Loading users...</div>;
  }

  if (users.length === 0) {
    return <div className="text-gray-500 text-sm py-8 text-center">No users found</div>;
  }

  const { isMobile } = useViewMode();
  const currentUserIsAdmin = currentUser && isAdmin(currentUser.systemRole);

  const conversation = (user: User) =>
    threadCountFor ? (
      <RecordConversations
        entityType="User"
        entityId={user.id}
        variant="icon"
        count={threadCountFor(user.id)}
        onActivity={onThreadActivity}
      />
    ) : null;

  if (isMobile) {
    return (
      <div className="space-y-3">
        {users.map((user) => (
          <div
            key={user.id}
            onClick={() => onSelectUser(user.id)}
            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
              selectedUserId === user.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <UserAvatar user={user} size="sm" />
              <span className="font-medium text-sm truncate">{user.name}</span>
              {conversation(user)}
              <span
                className={`text-xs px-2 py-0.5 rounded shrink-0 ml-auto ${
                  user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="text-xs text-gray-500 space-y-0.5 ml-8">
              <div className="truncate">{user.email ?? 'No email'}</div>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                {user.systemRole}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-500">
            <th className="pb-3 pr-4 font-medium">Name</th>
            <th className="pb-3 pr-4 font-medium">Email</th>
            <th className="pb-3 pr-4 font-medium">Role</th>
            <th className="pb-3 pr-4 font-medium">Status</th>
            <th className="pb-3 pr-4 font-medium">Joined</th>
            <th className="pb-3 pr-4 font-medium">Last Login</th>
            <th className="pb-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr
              key={user.id}
              onClick={() => onSelectUser(user.id)}
              className={`border-b border-gray-100 cursor-pointer transition-colors ${
                selectedUserId === user.id ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
            >
              <td className="py-3 pr-4">
                <div className="flex items-center gap-2">
                  <UserAvatar user={user} size="sm" />
                  {user.name}
                  {conversation(user)}
                </div>
              </td>
              <td className="py-3 pr-4 text-gray-600">{user.email ?? 'No email'}</td>
              <td className="py-3 pr-4">
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  {user.systemRole}
                </span>
              </td>
              <td className="py-3 pr-4">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="py-3 pr-4 text-gray-600">
                {new Date(user.createdAt).toLocaleDateString()}
              </td>
              <td className="py-3 pr-4 text-gray-600">
                {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
              </td>
              <td className="py-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectUser(user.id);
                    }}
                    className="text-gray-400 hover:text-primary-600 p-1"
                    aria-label="Edit user"
                    title="Edit user"
                  >
                    <Pencil size={16} />
                  </button>
                  {currentUserIsAdmin && user.isActive && currentUser.id !== user.id && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          await confirm({
                            title: 'Deactivate User',
                            message: `Deactivate ${user.name}? They will no longer be able to log in.`,
                            confirmLabel: 'Deactivate',
                          })
                        ) {
                          onDeactivateUser?.(user.id);
                        }
                      }}
                      className="text-gray-400 hover:text-red-600 p-1"
                      aria-label="Deactivate user"
                      title="Deactivate user"
                    >
                      <UserX size={16} />
                    </button>
                  )}
                  {currentUserIsAdmin && <AuditInfoTooltip entityType="User" entityId={user.id} />}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
